import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Car, CalendarCheck, CalendarClock, CalendarX, ChevronDown, ChevronRight, CircleAlert, CircleCheck, Clock, FileCheck, FileText,
  Gauge, Landmark, Package, Paperclip, Receipt, UserX, Wrench,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { api } from '@/lib/api'
import { Button, Skeleton } from '@/components/ui'
import { useDashboard } from '@/hooks/useDashboard'
import { useDashboardPropertyId } from '@/hooks/useDashboardScope'
import { useOverdueInvoicesReport } from '@/hooks/useReports'
import { useTeamToday, type TeamTodayRow } from '@/hooks/useShiftClock'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { formatMonthYear, localToday } from '@/lib/date'
import { formatBaht } from '@/lib/money'
import { useClosingMonth, type ClosingMonth } from '@/hooks/useClosingMonth'
import type { Parcel } from '@/types'

/**
 * EP-155: the one "to do" card ("ต้องจัดการ") at the top of every role's
 * dashboard. One row per thing the person can act on, shown only while its
 * count is above zero, in a fixed order; a row is a sentence with the count
 * and one action. Rows that carry a list (overdue tenants, expiring leases,
 * people who did not clock out, meters, parcels) open in place, top five,
 * closed by default. With nothing to do the card is one line.
 *
 * On a desktop a row shows its action as an sm button and a chevron for its
 * list; on a phone the whole row is the tap target (› goes to the page, ˅
 * opens the list, whose last line carries the action).
 */

export type DashboardRole = 'owner' | 'admin' | 'staff' | 'accountant'

const PREVIEW = 5

interface TodoRow {
  key: string
  icon: LucideIcon
  tone?: 'error' | 'warning'
  text: string
  action: { label: string; to: string; primary?: boolean }
  /** Rendered only while the row is open, so its query runs only then. */
  list?: () => ReactNode
}

const ROLES: Record<string, DashboardRole[]> = {
  forgotten: ['owner', 'admin'],
  slips: ['owner', 'admin', 'accountant'],
  parking_unpaid: ['owner', 'admin', 'staff'],
  expenses_confirm: ['owner', 'admin'],
  unidentified: ['accountant'],
  bank: ['accountant'],
  receipts: ['accountant'],
  not_billed: ['owner', 'admin', 'staff', 'accountant'],
  overdue: ['owner', 'admin', 'accountant'],
  expiring: ['owner', 'admin'],
  expired: ['owner', 'admin'],
  superseded: ['owner', 'admin'],
  meters: ['admin', 'staff'],
  parcels: ['admin', 'staff'],
  maintenance: ['admin', 'staff'],
}

export function TodoCard({ role }: { role: DashboardRole }) {
  const { t } = useTranslation('common')
  const formatDate = useDateFormatter()
  const { timezone, calendar } = useLocalizationConfig()
  const { i18n } = useTranslation()
  const isDesktop = useIsDesktop()
  const propertyId = useDashboardPropertyId()
  const { data: stats, isLoading, isError, refetch } = useDashboard(propertyId)
  const teamRole = role === 'owner' || role === 'admin'
  const { data: team } = useTeamToday(teamRole)
  const closing = useClosingMonth(role === 'accountant')
  const [open, setOpen] = useState<string | null>(null)

  const today = localToday(timezone)
  const can = (key: string) => ROLES[key].includes(role)

  if (isError && !stats) {
    return (
      <Shell title={t('dashboard.todo.title')}>
        <div className="flex items-center gap-3 text-sm" data-testid="todo-error">
          <span className="text-base-content/70">{t('dashboard.todo.load_failed')}</span>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>{t('dashboard.todo.retry')}</Button>
        </div>
      </Shell>
    )
  }

  if (isLoading || !stats) {
    return (
      <Shell title={t('dashboard.todo.title')}>
        <Skeleton className="h-5 w-64" />
      </Shell>
    )
  }

  const period = formatMonthYear(new Date(`${today.slice(0, 7)}-01T00:00:00`), { locale: i18n.language, calendar })
  const forgotten = (team?.users ?? []).filter((row) => row.open && row.open.work_date !== today)
  const ops = stats.operations
  const rows: TodoRow[] = []

  if (can('forgotten') && forgotten.length > 0) {
    const first = forgotten[0]
    rows.push({
      key: 'forgotten',
      icon: Clock,
      tone: 'error',
      text: forgotten.length === 1
        ? t('dashboard.todo.forgot_one', { name: first.user.name, date: formatDate(first.open?.work_date ?? null), time: first.open?.clock_in ?? '' })
        : t('dashboard.todo.forgot_many', { n: forgotten.length }),
      action: { label: t('dashboard.todo.forgot_action'), to: '/timesheet' },
      list: forgotten.length > 1 ? () => <ForgottenList rows={forgotten} /> : undefined,
    })
  }
  if (can('slips') && (stats.payments_pending_review ?? 0) > 0) {
    rows.push({
      key: 'slips',
      icon: Receipt,
      tone: 'warning',
      text: t('dashboard.todo.slips', { n: stats.payments_pending_review }),
      action: { label: t('dashboard.todo.slips_action'), to: '/payments?verification_status=pending&all_periods=true' },
    })
  }
  // EP-161 §2.4: cars that left without paying. The link opens the sessions
  // page of the dashboard's building, else of the oldest unpaid session, on
  // the same states the count uses, across every day.
  const parking = stats.parking_unpaid
  const parkingPropertyId = propertyId ?? parking?.property_id ?? null
  if (can('parking_unpaid') && parking && parking.count > 0 && parkingPropertyId !== null) {
    rows.push({
      key: 'parking_unpaid',
      icon: Car,
      tone: 'warning',
      text: t('dashboard.todo.parking_unpaid', { n: parking.count }),
      action: {
        label: t('dashboard.todo.parking_unpaid_action'),
        to: `/properties/${parkingPropertyId}/parking/sessions?state=awaiting_payment,rejected&all_days=1`,
      },
    })
  }
  if (can('expenses_confirm') && (stats.expenses_pending_confirmation ?? 0) > 0) {
    rows.push({
      key: 'expenses_confirm',
      icon: FileCheck,
      tone: 'warning',
      text: t('dashboard.todo.expenses_confirm', { n: stats.expenses_pending_confirmation }),
      action: { label: t('dashboard.todo.expenses_confirm_action'), to: '/expenses?status=pending_confirmation' },
    })
  }
  if (role === 'accountant' && closing.manifest) {
    const m = closing.manifest
    if (m.payments_unidentified_count > 0) rows.push({
      key: 'unidentified', icon: UserX,
      text: t('dashboard.todo.unidentified', { n: m.payments_unidentified_count }),
      action: { label: t('dashboard.todo.unidentified_action'), to: '/payments?unidentified=true' },
    })
    if (m.bank_rows_unmatched_count > 0) rows.push({
      key: 'bank', icon: Landmark,
      text: t('dashboard.todo.bank', { n: m.bank_rows_unmatched_count }),
      action: { label: t('dashboard.todo.bank_action'), to: '/bank-reconciliation' },
    })
    if (m.expenses_missing_receipt_count > 0) rows.push({
      key: 'receipts', icon: Paperclip,
      text: t('dashboard.todo.receipts', { n: m.expenses_missing_receipt_count }),
      action: { label: t('dashboard.todo.receipts_action'), to: receiptsLink(closing) },
    })
  }
  if (can('not_billed') && stats.active_leases > 0 && stats.revenue.billed_this_month === 0) {
    rows.push({
      key: 'not_billed',
      icon: FileText,
      tone: 'warning',
      text: t('dashboard.todo.not_billed', { period }),
      action: { label: t('dashboard.todo.not_billed_action'), to: '/invoices/generate', primary: true },
    })
  }
  if (can('overdue') && stats.overdue_invoices > 0) {
    rows.push({
      key: 'overdue',
      icon: CircleAlert,
      tone: 'error',
      text: t('dashboard.todo.overdue', {
        n: stats.overdue_invoices,
        amount: stats.revenue.overdue_amount === undefined ? '' : formatBaht(stats.revenue.overdue_amount, { decimals: 0 }),
      }).trim(),
      action: { label: t('dashboard.todo.overdue_action'), to: '/reports/collections/overdue' },
      list: () => <OverdueList propertyId={propertyId} />,
    })
  }
  const expiring = stats.expiring_leases ?? []
  if (can('expiring') && expiring.length > 0) {
    rows.push({
      key: 'expiring',
      icon: CalendarClock,
      tone: 'warning',
      text: t('dashboard.todo.expiring', { n: expiring.length }),
      action: { label: t('dashboard.todo.expiring_action'), to: '/leases' },
      list: () => (
        <ListLines
          lines={expiring.map((lease) => ({
            key: lease.id,
            to: `/leases/${lease.id}`,
            left: [lease.unit_name, lease.customer_name].filter(Boolean).join('  '),
            right: t('dashboard.todo.ends', { date: formatDate(lease.ended_at) }),
          }))}
          more={{ to: '/leases', label: t('dashboard.todo.view_all', { n: expiring.length }) }}
        />
      ),
    })
  }
  // EP-162 item 11: rental leases past their end date that nobody renewed or
  // ended. Each line opens the lease, where Renew and End are.
  const expired = stats.expired_leases ?? []
  if (can('expired') && expired.length > 0) {
    rows.push({
      key: 'expired',
      icon: CalendarX,
      tone: 'error',
      text: t('dashboard.todo.expired', { n: expired.length }),
      action: { label: t('dashboard.todo.expired_action'), to: expired.length === 1 ? `/leases/${expired[0].id}` : '/leases' },
      list: () => (
        <ListLines
          lines={expired.map((lease) => ({
            key: lease.id,
            to: `/leases/${lease.id}`,
            left: [lease.unit_name, lease.customer_name].filter(Boolean).join('  '),
            right: t('dashboard.todo.expired_since', { date: formatDate(lease.ended_at), count: lease.days_since }),
            tone: 'error' as const,
          }))}
          more={{ to: '/leases', label: t('dashboard.todo.view_all', { n: expired.length }) }}
        />
      ),
    })
  }
  const superseded = stats.superseded_leases ?? []
  if (can('superseded') && superseded.length > 0) {
    rows.push({
      key: 'superseded',
      icon: CalendarCheck,
      tone: 'warning',
      text: t('dashboard.todo.superseded', { n: superseded.length }),
      action: { label: t('dashboard.todo.superseded_action'), to: superseded.length === 1 ? `/leases/${superseded[0].id}` : '/leases' },
      list: () => (
        <ListLines
          lines={superseded.map((lease) => ({
            key: lease.id,
            to: `/leases/${lease.id}`,
            left: [lease.unit_name, lease.lease_code].filter(Boolean).join('  '),
            right: t('dashboard.todo.superseded_newer', { date: formatDate(lease.ended_at), code: lease.newer_lease_code }),
          }))}
          more={{ to: '/leases', label: t('dashboard.todo.view_all', { n: superseded.length }) }}
        />
      ),
    })
  }
  const meters = stats.meters_to_read
  if (can('meters') && meters && meters.count > 0) {
    rows.push({
      key: 'meters',
      icon: Gauge,
      text: t('dashboard.todo.meters', { n: meters.count }),
      action: { label: t('dashboard.todo.meters_action'), to: '/meter-readings' },
      list: () => <MetersList meters={meters} />,
    })
  }
  if (can('parcels') && ops?.parcels_used && ops.waiting_parcel_count > 0) {
    rows.push({
      key: 'parcels',
      icon: Package,
      text: t('dashboard.todo.parcels', { n: ops.waiting_parcel_count }),
      action: { label: t('dashboard.todo.parcels_action'), to: '/parcels' },
      list: () => <ParcelsList propertyId={propertyId} total={ops.waiting_parcel_count} />,
    })
  }
  if (can('maintenance') && ops?.maintenance_used && ops.open_maintenance_count > 0) {
    rows.push({
      key: 'maintenance',
      icon: Wrench,
      text: t('dashboard.todo.maintenance', { n: ops.open_maintenance_count }),
      action: { label: t('dashboard.todo.maintenance_action'), to: '/maintenances' },
    })
  }

  if (rows.length === 0) {
    return (
      <Shell title={t('dashboard.todo.title')}>
        <p className="flex items-center gap-2 text-sm text-base-content/70" data-testid="todo-nothing">
          <CircleCheck className="w-4 h-4 text-success" aria-hidden />
          {t('dashboard.todo.nothing')}
        </p>
      </Shell>
    )
  }

  return (
    <Shell title={t('dashboard.todo.title')} count={rows.length}>
      <ul className="divide-y divide-base-200" data-testid="todo-rows">
        {rows.map((row) => {
          const isOpen = open === row.key
          const toggle = () => setOpen(isOpen ? null : row.key)
          const Icon = row.icon
          const icon = (
            <Icon
              className={`w-4 h-4 shrink-0 ${row.tone === 'error' ? 'text-error' : row.tone === 'warning' ? 'text-warning' : 'text-base-content/60'}`}
              aria-hidden
            />
          )
          return (
            <li key={row.key} data-testid={`todo-${row.key}`}>
              {isDesktop ? (
                // Desktop (owner, 2026-10-07): the chevron just left of the
                // action button, an empty slot of its width where a row has no
                // list so the buttons line up; every action button one width.
                <div className="flex items-center gap-3 py-2">
                  {icon}
                  <span className="flex-1 min-w-0 text-sm">{row.text}</span>
                  {row.list ? (
                    <button type="button" onClick={toggle} aria-expanded={isOpen}
                      aria-label={isOpen ? t('dashboard.todo.hide_list') : t('dashboard.todo.show_list')}
                      className="flex w-6 h-6 shrink-0 items-center justify-center rounded text-base-content/60 hover:bg-base-200"
                      data-testid={`todo-${row.key}-toggle`}>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <span className="w-6 shrink-0" aria-hidden />
                  )}
                  <Button as={Link} to={row.action.to} size="sm" variant="primary" outline={!row.action.primary}
                    className="w-36 shrink-0 justify-center" data-testid={`todo-${row.key}-action`}>
                    {row.action.label}
                  </Button>
                </div>
              ) : row.list ? (
                <button type="button" onClick={toggle} aria-expanded={isOpen}
                  className="flex w-full items-center gap-3 py-3 text-left" data-testid={`todo-${row.key}-toggle`}>
                  {icon}
                  <span className="flex-1 min-w-0 text-sm">{row.text}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 text-base-content/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <Link to={row.action.to} className="flex items-center gap-3 py-3 no-underline text-base-content"
                  data-testid={`todo-${row.key}-action`}>
                  {icon}
                  <span className="flex-1 min-w-0 text-sm">{row.text}</span>
                  <ChevronRight className="w-4 h-4 shrink-0 text-base-content/60" aria-hidden />
                </Link>
              )}
              {isOpen && row.list && (
                <div className="pb-2" data-testid={`todo-${row.key}-list`}>
                  {row.list()}
                  {!isDesktop && (
                    <Button as={Link} to={row.action.to} size="sm" variant="primary" outline className="mt-2 w-full">
                      {row.action.label}
                    </Button>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </Shell>
  )
}

function Shell({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-base-200 bg-base-100 px-4 py-3 space-y-1" data-testid="todo-card" aria-label={title}>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-base-content/80">{title}</h2>
        {count !== undefined && <span className="text-xs text-base-content/60 tabular-nums" data-testid="todo-count">{count}</span>}
      </div>
      {children}
    </section>
  )
}

interface Line { key: number | string; to: string; left: string; right?: ReactNode; tone?: 'error' }

function ListLines({ lines, more }: { lines: Line[]; more?: { to: string; label: string } }) {
  return (
    <ul className="text-sm">
      {lines.slice(0, PREVIEW).map((line) => (
        <li key={line.key}>
          <Link to={line.to} className="flex items-center gap-3 py-1.5 no-underline text-base-content hover:text-primary">
            <span className="flex-1 min-w-0 truncate">{line.left}</span>
            {line.right && <span className={`shrink-0 text-xs ${line.tone === 'error' ? 'text-error' : 'text-base-content/60'}`}>{line.right}</span>}
          </Link>
        </li>
      ))}
      {more && lines.length > PREVIEW && (
        <li><Link to={more.to} className="block py-1.5 text-primary text-xs">{more.label}</Link></li>
      )}
    </ul>
  )
}

function ForgottenList({ rows }: { rows: TeamTodayRow[] }) {
  const { t } = useTranslation('common')
  const formatDate = useDateFormatter()
  return (
    <ListLines
      lines={rows.map((row) => ({
        key: row.user.id,
        to: '/timesheet',
        left: row.user.name,
        right: t('dashboard.team_today.open_since', { date: formatDate(row.open?.work_date ?? null), time: row.open?.clock_in ?? '' }),
        tone: 'error' as const,
      }))}
    />
  )
}

function OverdueList({ propertyId }: { propertyId: number | null }) {
  const { t } = useTranslation('common')
  const { data, isLoading } = useOverdueInvoicesReport({ propertyId: propertyId ?? undefined })
  if (isLoading) return <Skeleton className="h-4 w-48" />
  const rows = data?.rows ?? []
  return (
    <ListLines
      lines={rows.map((row) => ({
        key: row.invoice_id,
        to: `/invoices/${row.invoice_id}`,
        left: `${row.unit_number}  ${row.tenant_name}`,
        right: <>{formatBaht(row.balance)} · {t('dashboard.overdue_days_late', { count: Math.floor(row.days_late) })}</>,
        tone: 'error' as const,
      }))}
      more={{ to: '/reports/collections/overdue', label: t('dashboard.todo.view_all', { n: rows.length }) }}
    />
  )
}

function MetersList({ meters }: { meters: NonNullable<ReturnType<typeof useDashboard>['data']>['meters_to_read'] }) {
  const { t } = useTranslation('common')
  const { t: tm } = useTranslation('meters')
  if (!meters) return null
  return (
    <ul className="text-sm">
      {meters.items.map((m) => (
        <li key={m.meter_id} className="flex items-center gap-3 py-1">
          <span className="flex-1 min-w-0 truncate">{m.unit_number}  {tm(`types.${m.meter_type}`)}</span>
          <Button as={Link} to={`/meters/${m.meter_id}`} size="xs" variant="ghost" className="shrink-0">
            + {t('dashboard.todo.meter_record')}
          </Button>
        </li>
      ))}
      {meters.count > meters.items.length && (
        <li><Link to="/meter-readings" className="block py-1.5 text-primary text-xs">{t('dashboard.todo.view_all', { n: meters.count })}</Link></li>
      )}
    </ul>
  )
}

function ParcelsList({ propertyId, total }: { propertyId: number | null; total: number }) {
  const { t } = useTranslation('common')
  const { data, isLoading } = useQuery({
    queryKey: ['parcels', 'waiting', propertyId],
    queryFn: async () => {
      const params: Record<string, unknown> = { status: ['pending', 'notified'] }
      if (propertyId) params.property_id = propertyId
      const { data } = await api.get<{ data: Parcel[] }>('/api/parcels', { params })
      return data.data
    },
  })
  if (isLoading) return <Skeleton className="h-4 w-48" />
  const parcels = data ?? []
  return (
    <ListLines
      lines={parcels.map((p) => ({
        key: p.id,
        to: `/parcels/${p.id}`,
        left: [p.unit?.number, p.recipient_name || p.customer?.name, p.tracking_number].filter(Boolean).join('  '),
      }))}
      more={total > PREVIEW ? { to: '/parcels', label: t('dashboard.todo.view_all', { n: total }) } : undefined}
    />
  )
}

function receiptsLink(closing: ClosingMonth): string {
  const m = closing.manifest
  if (!m) return '/expenses?missing_receipt=true'
  return `/expenses?missing_receipt=true&from=${m.window_from}&to=${m.window_to}`
}
